/* eslint-disable max-lines */
import { createTheme, PaletteColorOptions } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface Palette {
    brand?: PaletteColor;
  }

  interface PaletteOptions {
    brand?: PaletteColorOptions;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    customColor?: PaletteColor;
  }
  interface PalettePrimary {
    [key: string]: string;
    main: string;
  }
  interface PaletteColor {
    [key: string]: string;
    main: string;
  }
  interface PaletteOptions {
    customColor?: PaletteColorOptions;
  }
  interface Theme {
    spacer?: Record<SpacerKeys, string>;
    weights?: Record<FontWeightsKeys, FontWeights>;
  }
  type SpacerKeys =
    | "xxxxx-large"
    | "xxxx-large"
    | "xxx-large"
    | "xx-large"
    | "x-large"
    | "large"
    | "medium"
    | "small";
  type FontWeights = 500 | 400 | 300;
  type FontWeightsKeys = "Medium" | "Regular" | "Small";
  interface ThemeOptions {
    spacer?: Record<SpacerKeys, string>;
    weights?: Record<FontWeightsKeys, FontWeights>;
  }
}

export const Theme = createTheme({
  spacing: (v: number) => 4 * v,
  spacer: {
    "xxxxx-large": "3.8rem",
    "xxxx-large": "3rem",
    "xxx-large": "2.5rem",
    "xx-large": "2rem",
    "x-large": "1.5rem",
    large: "1.25rem",
    medium: "1rem",
    small: "0.875rem",
  },
  weights: {
    Medium: 500,
    Regular: 400,
    Small: 300,
  },
  typography: {
    fontFamily: "Inter Variable",
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        style: {
          borderRadius: "8px",
          fontWeight: 700,
          textTransform: "capitalize",
        },
      },
    },

MuiTextField: {
  styleOverrides: {
    root: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "#F4F6F8",
        "& fieldset": { borderColor: "#DFE3E8" },

        "&:hover fieldset": {
          borderColor: "#DFE3E8"
        },

        "&.Mui-focused fieldset": {
          borderColor: "#465A72"
        },

        "& .MuiInputBase-input": {
          color: "#465A72",
        },

        "& .MuiInputBase-inputMultiline": {
          color: "#465A72",
        },
      },

      "& .MuiInputLabel-root": {
        color: "#818181ff",
        fontSize: 14,
        fontWeight: 500,
        transition: "all 200ms ease",
      },

      // 👇 color rojo cuando está enfocado
      "& .MuiInputLabel-root.Mui-focused": {
        color: "#465A72",
      },
    },
  },
},


    MuiTypography: {
      styleOverrides: {
        h1: { fontSize: "64px", lineHeight: 1.25, fontWeight: 800 },
        h2: { fontSize: "48px", lineHeight: 1.33, fontWeight: 800 },
        h3: { fontSize: "35px", lineHeight: 1.5, fontWeight: 800 },
        h4: { fontSize: "24px", lineHeight: 1.5, fontWeight: 700 },
        h5: { fontSize: "19px", lineHeight: 1.47, fontWeight: 700 },
        h6: { fontSize: "18px", lineHeight: 1.56, fontWeight: 600 },
        subtitle1: {
          fontSize: "17px",
          fontWeight: 600,
        },
        subtitle2: {
          fontSize: "14px",
          fontWeight: 600,
        },
        body1: {
          fontSize: "15px",
          fontWeight: 400,
        },
        body2: {
          fontSize: "13px",
          fontWeight: 500,
        },
        caption: {
          fontSize: "12px",
          fontWeight: 400,
        },
        overline: {
          fontSize: "12px",
          fontWeight: 700,
        },
        button: {
          fontSize: "15px",
          fontWeight: 500,
        },
      },
    },
  },

  palette: {
    brand: {
      light: "rgba(255, 255, 255, 0.90)",
      main: "#a1a4a714",
    },

    primary: {
      "100": "#F8D6C9",
      "200": "#F5C9B7",
      "300": "#F3BBA5",
      "400": "#EEA081",
      "500": "#EB936F",
      "600": "#E9855D",
      "700": "#E7774B",
      "800": "#E46A3A",
      "900": "#E25C28",
      main: "#F8D8CB",
    },
    secondary: {
      "100": "#637381",
      "200": "#556E8B",
      "300": "#4E647E",
      "400": "#465A72",
      "500": "#3E5065",
      "600": "#364659",
      "700": "#2F3C4C",
      "800": "#27323F",
      "900": "#1F2833",
      main: "#1d1d1f",
    },
    info: {
      main: "#006C9C",
    },
    success: {
      "100": "#B9F3CE",
      "200": "#A8F0C2",
      "300": "#96EDB6",
      "400": "#85EAAA",
      "500": "#73E79E",
      "600": "#62E492",
      "700": "#51E186",
      "800": "#3FDE7A",
      "900": "#118D57",
      main: "#22C55E",
    },
    warning: {
      main: "#FF5630",
    },
    error: {
      main: "#B71D18",
    },
    customColor: {
      main: "#F4F6F8",
      light: "#DFE3E8",
      dark: "#919eab33",
    },
    text: { disabled: "#86868b" },
    background: { paper: "rgba(255, 255, 255, 0.85)" },
  },

  breakpoints: {
    values: {
      xs: 0,
      sm: 750,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

export type CustomTheme = typeof Theme;
